import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  useDroppable,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { PlusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { PropsWithChildren, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { Button, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateReportTypeChoices,
  ReportPageLocationEnums,
  ReportTemplateType,
} from "@api/graphql/types-and-hooks";
import { HoveredAddButton } from "@components/HoveredAddButton/HoveredAddButton";
import { TEMPLATE_EDITOR_TABS, TemplateEditorTab } from "@pages/Templates/TemplateEditor";
import { ConfirmDeleteAiFieldDialog } from "@pages/Templates/components/Dialog/ConfirmDeleteAiFieldDialog";
import { FieldEditor } from "@pages/Templates/components/FieldEditor/FieldEditor";
import { SectionDivider } from "@pages/Templates/components/SectionDivider/SectionDivider";
import { TemplateElementHeader } from "@pages/Templates/components/TemplateElementHeader/TemplateElementHeader";
import {
  TreatmentPlanTemplateEditor,
  TreatmentPlanTemplateState,
} from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditor";
import { buildNewSectionField, getPageLocations, sectionsAfterDrop } from "@pages/Templates/utils";
import { ReportTemplateSection, ReportTemplateSectionField, useTemplate } from "@providers/TemplateEditorProvider";

interface TemplateStructureProps extends PropsWithChildren {
  reportTemplate: ReportTemplateType;
  reportTemplateSections: ReportTemplateSection[];
  setReportTemplateSections: (sections: ReportTemplateSection[]) => void;
  // Handlers read these: a save can replace the sections, and swap ids, before the next render.
  getLatestSections: () => ReportTemplateSection[];
  resolveItemId: (id: string) => string;
  // Saved id -> the id the item was added with, so a save's id swap doesn't remount or collapse it.
  itemKeys: Record<string, string>;
  isCollapsed?: { value: boolean };
  readonly: boolean;
  activeTab?: TemplateEditorTab;
  setActiveTab?: (tab: TemplateEditorTab) => void;
  onChange: () => void;
  onTreatmentPlanTemplateUpdate?: (data: TreatmentPlanTemplateState) => void;
}

export const InitialAbPageLocations: Record<string, string> = {
  [ReportPageLocationEnums.ReportPart_1]: "Report Part 1",
  [ReportPageLocationEnums.ReportPart_2]: "Report Part 2",
};

export const DiagnosisEvaluationPageLocations: Record<string, string> = {
  [ReportPageLocationEnums.ReportPart_1]: "Report Part 1",
  [ReportPageLocationEnums.ReportPart_2]: "Report Part 2",
  [ReportPageLocationEnums.Conclusions]: "Conclusions",
  [ReportPageLocationEnums.Recommendations]: "Recommendations",
};

export const SinglePage = "Single_Page";

// Component for droppable empty page zones
const EmptyPageDropZone: React.FC<{ pageLocation: string }> = ({ pageLocation }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-page-${pageLocation}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[100px] flex items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
        isOver ? "border-green-500 bg-green-50" : "border-limestone-200 bg-limestone-50"
      }`}
    >
      <Small colorType="secondary">Drop section here or click button below to add a section</Small>
    </div>
  );
};

// Component for droppable empty section zones (allows dragging fields into empty sections)
const EmptySectionDropZone: React.FC<{
  sectionId: string;
  onAddField: () => void;
  readonly: boolean;
}> = ({ sectionId, onAddField, readonly }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-section-${sectionId}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "size-full flex flex-col items-center pt-4 pb-8 pr-4 gap-2 rounded-lg transition-colors",
        isOver && "border-2 border-dashed border-green-500 bg-green-50",
      )}
    >
      <Paragraph displayType="loud" colorType="primary">
        {isOver ? "Drop field here" : "No Fields in this Section"}
      </Paragraph>
      <Small colorType="secondary">
        {isOver
          ? "Release to move field into this section"
          : "Add your first field to get started configuring this section."}
      </Small>
      {!isOver && !readonly && (
        <Button
          appearance="primary"
          className="rounded mt-2"
          onClick={onAddField}
          text={
            <div className="flex items-center text-white ">
              <PlusIcon className="mr-1 h-4 w-4" />
              <Small colorType="primary" className="text-white">
                Add Field
              </Small>
            </div>
          }
        />
      )}
    </div>
  );
};

export const TemplateStructure: React.FC<TemplateStructureProps> = ({
  reportTemplate,
  reportTemplateSections: renderedSections,
  setReportTemplateSections,
  getLatestSections,
  resolveItemId,
  itemKeys,
  isCollapsed = { value: false },
  readonly,
  activeTab = TEMPLATE_EDITOR_TABS.NARRATIVE,
  setActiveTab,
  onChange,
  onTreatmentPlanTemplateUpdate,
}) => {
  const itemKey = (id: string) => itemKeys[id] ?? id;

  const [draggedField, setDraggedField] = useState<ReportTemplateSectionField | null>();
  const [draggedSection, setDraggedSection] = useState<ReportTemplateSection | null>();
  // Track which items are newly created (should start expanded)
  const [newlyCreatedItems, setNewlyCreatedItems] = useState<Set<string>>(new Set());
  const [itemCollapseStates, setItemCollapseStates] = useState<Record<string, boolean>>({});
  const handleCollapseChange = (itemId: string, isCollapsed: boolean) => {
    const key = itemKey(itemId);
    setItemCollapseStates((prev) => ({
      ...prev,
      [key]: isCollapsed,
    }));

    // Remove from newly created items when manually collapsed
    if (isCollapsed) {
      setNewlyCreatedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const { setSections: setTemplateContextSections } = useTemplate();

  // Sync sections with template context for use in child components
  useEffect(() => {
    setTemplateContextSections(renderedSections);
  }, [renderedSections, setTemplateContextSections]);

  const [pendingDeleteAiField, setPendingDeleteAiField] = useState<{
    sectionId: string;
    fieldId: string;
    fieldTitle: string;
  } | null>(null);

  const updateSections = (newSections: ReportTemplateSection[]) => {
    try {
      setReportTemplateSections(newSections);
      onChange();
    } catch (error) {
      console.error("Error in updateSections:", error);
    }
  };

  // Handlers below start from getLatestSections() and resolveItemId() rather than this render's props
  // and ids, so an event after a save swapped ids (before the re-render, or mid-drag) edits the saved copy.

  // Handle global collapse/expand all
  useEffect(() => {
    // Clear newly created items when global collapse/expand is triggered
    setNewlyCreatedItems(new Set());
  }, [isCollapsed.value]);

  // Helper function to determine if a section should be collapsed
  const getSectionCollapsedState = (sectionId: string) => {
    const key = itemKey(sectionId);
    // Check if there's a specific state for this item
    if (itemCollapseStates.hasOwnProperty(key)) {
      return { value: itemCollapseStates[key] };
    }

    // If newly created, start expanded
    if (newlyCreatedItems.has(key)) {
      return { value: false };
    }

    // Use global state
    return isCollapsed;
  };

  // Helper function to determine if a field should be collapsed
  const getFieldCollapsedState = (fieldId: string) => {
    const key = itemKey(fieldId);
    // Same logic as above
    if (itemCollapseStates.hasOwnProperty(key)) {
      return { value: itemCollapseStates[key] };
    }

    if (newlyCreatedItems.has(key)) {
      return { value: false };
    }

    return isCollapsed;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { over } = event;
    const newSections =
      over &&
      sectionsAfterDrop(getLatestSections(), {
        activeId: resolveItemId(String(event.active.id)),
        overId: resolveItemId(String(over.id)),
        rawOverId: over.id,
        isSectionDrag: !!draggedSection,
        isFieldDrag: !!draggedField,
        resolveItemId,
      });
    if (newSections) updateSections(newSections);
    setDraggedSection(null);
    setDraggedField(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const sections = getLatestSections();
    const activeId = resolveItemId(String(event.active.id));
    const section = sections.find((section) => section.id === activeId);
    const field = sections.flatMap((s) => s.fields).find((field) => field.id === activeId);
    if (field) {
      setDraggedField(field);
    } else if (section) {
      setDraggedSection(section);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const sections = getLatestSections();
    const { active, over } = event;

    if (!draggedField || !over) return;

    // Empty section drop zones are handled by handleDragEnd; visual feedback comes from useDroppable's isOver
    if (typeof over.id === "string" && over.id.startsWith("empty-section-")) {
      return;
    }

    const activeId = resolveItemId(String(active.id));
    const overId = resolveItemId(String(over.id));

    // Existing cross-section field drag logic
    const sourceSection = sections.find((section) => section.fields.some((field) => field.id === activeId));
    const destinationSection = sections.find((section) => section.fields.some((field) => field.id === overId));

    if (sourceSection && destinationSection && sourceSection !== destinationSection) {
      const destinationIndex = destinationSection.fields.findIndex((field) => field.id === overId);

      if (destinationIndex !== -1) {
        const newSections = [...sections];

        const [movedField] = sourceSection.fields.splice(
          sourceSection.fields.findIndex((field) => field.id === activeId),
          1,
        );

        destinationSection.fields.splice(destinationIndex, 0, movedField);

        let rankOrder = 1;
        destinationSection.fields = destinationSection.fields.map((field) => ({
          ...field,
          rankOrder: rankOrder++,
        }));

        updateSections(newSections);
      }
    }
  };

  const handleDeleteSection = (heldSectionId: string) => {
    const sectionId = resolveItemId(heldSectionId);
    const remainingSections = getLatestSections().filter((section) => section.id !== sectionId);

    const newSections = remainingSections.map((section) => {
      // find all sections with the same page location; sections are already ordered so rankOrder will follow nicely
      const sectionsWithSamePageLocation = remainingSections
        .slice(0, remainingSections.indexOf(section) + 1)
        .filter((s) => s.pageLocation === section.pageLocation);

      return {
        ...section,
        rankOrder: sectionsWithSamePageLocation.length,
      };
    });

    // Clean up newly created items tracking
    setNewlyCreatedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemKey(sectionId));
      return newSet;
    });

    updateSections(newSections);
  };

  const handleDeleteField = (heldSectionId: string, heldFieldId: string) => {
    const sectionId = resolveItemId(heldSectionId);
    const fieldId = resolveItemId(heldFieldId);
    const newSections = [...getLatestSections()];
    const sectionIndex = newSections.findIndex((section) => section.id === sectionId);
    if (sectionIndex !== -1) {
      const section = newSections[sectionIndex];

      const newFields = section.fields.filter((field) => field.id !== fieldId);
      newSections[sectionIndex] = { ...section, fields: newFields };

      // Clean up newly created items tracking
      setNewlyCreatedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemKey(fieldId));
        return newSet;
      });

      updateSections(newSections);
    }
  };

  const handleDeleteFieldWithAiCheck = (sectionId: string, fieldId: string) => {
    const currentSectionId = resolveItemId(sectionId);
    const currentFieldId = resolveItemId(fieldId);
    const section = getLatestSections().find((s) => s.id === currentSectionId);
    const field = section?.fields.find((f) => f.id === currentFieldId);

    if (field?.isAi) {
      const normalizedFieldTitle = field.title?.trim() || "Untitled Field";

      setPendingDeleteAiField({
        sectionId,
        fieldId,
        fieldTitle: normalizedFieldTitle,
      });
      return;
    }

    handleDeleteField(sectionId, fieldId);
  };

  const handleAddSection = (pageLocation: ReportPageLocationEnums, index: number) => {
    const sections = getLatestSections();
    try {
      const newSectionId = `section-${Date.now()}`;
      const newSection = {
        id: newSectionId,
        rankOrder: index + 1,
        pageLocation,
        title: "New Section",
        isDefault: false,
        fields: [],
        inputDependency: null,
      };

      // Get indices of all sections for this pageLocation
      const indices = sections
        .map((s, i) => ((s.pageLocation as unknown as ReportPageLocationEnums) === pageLocation ? i : -1))
        .filter((i) => i !== -1);

      let insertAt;
      if (index >= indices.length) {
        // Insert after the last section for this pageLocation
        insertAt = indices.length > 0 ? indices[indices.length - 1] + 1 : sections.length;
      } else {
        // Insert before the section at the given index for this pageLocation
        insertAt = indices[index];
      }

      const newSections = [...sections];
      newSections.splice(insertAt, 0, newSection as unknown as ReportTemplateSection);

      // Update rankOrder for all sections in this page location
      let rankOrder = 1;
      const updatedSections = newSections.map((section) => {
        if ((section.pageLocation as unknown as ReportPageLocationEnums) === pageLocation) {
          return { ...section, rankOrder: rankOrder++ };
        }
        return section;
      });

      setNewlyCreatedItems((prev) => {
        const newSet = new Set(prev).add(newSectionId);
        return newSet;
      });

      updateSections(updatedSections);
    } catch (error) {
      console.error("Error in handleAddSection:", error);
    }
  };

  const handleAddField = (heldSectionId: string, index: number) => {
    const sectionId = resolveItemId(heldSectionId);
    const newSections = [...getLatestSections()];
    const sectionIndex = newSections.findIndex((section) => section.id === sectionId);
    if (sectionIndex !== -1) {
      const section = newSections[sectionIndex];
      const newFieldId = `field-${Date.now()}`;
      const newField = buildNewSectionField({ id: newFieldId, rankOrder: index + 1 });

      // Insert the new field at the specified index
      const newFields = [...section.fields];
      newFields.splice(index, 0, newField);

      // Update rankOrder for all fields
      const updatedFields = newFields.map((field, idx) => ({
        ...field,
        rankOrder: idx + 1,
      }));

      newSections[sectionIndex] = { ...section, fields: updatedFields };

      // Mark new field and parent section as newly created (should start expanded)
      setNewlyCreatedItems((prev) => new Set(prev).add(newFieldId).add(itemKey(sectionId)));

      updateSections(newSections);
    }
  };

  const handleDuplicateSection = (heldSectionId: string) => {
    const sectionId = resolveItemId(heldSectionId);
    const newSections = [...getLatestSections()];
    const sectionIndex = newSections.findIndex((section) => section.id === sectionId);
    if (sectionIndex !== -1) {
      const section = newSections[sectionIndex];
      const newSectionId = `section-${Date.now()}`;
      const newSection = {
        ...section,
        id: newSectionId,
        title: `${section.title} (Copy)`,
        rankOrder: section.rankOrder + 1,
        fields: section.fields.map((field) => ({
          ...field,
          id: `field-${Date.now()}-${field.id}`,
        })),
      };
      newSections.splice(sectionIndex + 1, 0, newSection);

      // Renumber the page so the copy stays where it was inserted once the saved order comes back
      let rankOrder = 1;
      const updatedSections = newSections.map((s) =>
        s.pageLocation === section.pageLocation ? { ...s, rankOrder: rankOrder++ } : s,
      );

      // Mark duplicated section as newly created
      setNewlyCreatedItems((prev) => new Set(prev).add(newSectionId));

      updateSections(updatedSections);
    }
  };

  const handleSectionUpdate = (heldSectionId: string, updates: Partial<ReportTemplateSection>) => {
    const sectionId = resolveItemId(heldSectionId);
    const currentSections = getLatestSections();
    const newSections = [...currentSections];

    const sectionIndex = newSections.findIndex((s) => s.id === sectionId);
    if (sectionIndex === -1) return;

    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      ...updates,
    };

    updateSections(newSections);
  };

  const handleFieldUpdate = (
    heldSectionId: string,
    heldFieldId: string,
    updatedField: Partial<ReportTemplateSectionField>,
  ) => {
    const sectionId = resolveItemId(heldSectionId);
    const fieldId = resolveItemId(heldFieldId);
    const currentSections = getLatestSections();
    const newSections = [...currentSections];

    const sectionIndex = newSections.findIndex((section) => section.id === sectionId);
    if (sectionIndex === -1) return;

    const section = newSections[sectionIndex];
    const fieldIndex = section.fields.findIndex((field) => field.id === fieldId);
    if (fieldIndex === -1) return;

    const newFields = [...section.fields];

    newFields[fieldIndex] = {
      ...newFields[fieldIndex],
      ...updatedField,
      // FieldEditor passes the id it rendered with, which may predate the save's id swap.
      id: newFields[fieldIndex].id,
    };

    newSections[sectionIndex] = {
      ...section,
      fields: newFields,
    };

    updateSections(newSections);
  };

  const handleDuplicateField = (heldSectionId: string, heldFieldId: string) => {
    const sectionId = resolveItemId(heldSectionId);
    const fieldId = resolveItemId(heldFieldId);
    const newSections = [...getLatestSections()];
    const sectionIndex = newSections.findIndex((section) => section.id === sectionId);
    if (sectionIndex !== -1) {
      const section = newSections[sectionIndex];
      const fieldIndex = section.fields.findIndex((field) => field.id === fieldId);
      if (fieldIndex !== -1) {
        const field = section.fields[fieldIndex];
        const newFieldId = `field-${Date.now()}`;
        const newField = {
          ...field,
          id: newFieldId,
          title: `${field.title} (Copy)`,
        };
        const newFields = [...section.fields];
        newFields.splice(fieldIndex + 1, 0, newField);
        // Renumber so the copy stays below its source once the saved order comes back
        newSections[sectionIndex] = {
          ...section,
          fields: newFields.map((f, idx) => ({ ...f, rankOrder: idx + 1 })),
        };

        // Mark duplicated field as newly created
        setNewlyCreatedItems((prev) => new Set(prev).add(newFieldId));

        updateSections(newSections);
      }
    }
  };

  const pageLocations = getPageLocations(reportTemplate.reportType as ApiReportTemplateReportTypeChoices);

  if (activeTab === TEMPLATE_EDITOR_TABS.PROGRAMMING && reportTemplate.treatmentPlanTemplate) {
    return (
      <div className="mt-4">
        <TreatmentPlanTemplateEditor
          treatmentPlanTemplate={reportTemplate.treatmentPlanTemplate}
          readonly={readonly}
          onChange={onChange}
          onUpdate={(data) => onTreatmentPlanTemplateUpdate?.(data)}
        />
      </div>
    );
  }

  return (
    <div>
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={(event) => handleDragStart(event)}
        onDragEnd={(event) => handleDragEnd(event)}
        onDragOver={(event) => handleDragOver(event)}
      >
        {pageLocations.map((pageLocation) => {
          const sectionsForLocation = renderedSections.filter(
            (section) => section.pageLocation === pageLocation || pageLocation === SinglePage,
          );

          return (
            <div key={pageLocation}>
              {pageLocation !== SinglePage && (
                <SectionDivider
                  label={
                    reportTemplate.reportType === ApiReportTemplateReportTypeChoices.InitialAssessment
                      ? InitialAbPageLocations[pageLocation]
                      : reportTemplate.reportType === ApiReportTemplateReportTypeChoices.DiagnosisEvaluation
                        ? DiagnosisEvaluationPageLocations[pageLocation]
                        : SinglePage
                  }
                />
              )}
              <SortableContext items={sectionsForLocation}>
                {sectionsForLocation.length > 0 ? (
                  sectionsForLocation.map((section, sectionIndex) => (
                    <div
                      className={`template-section-container relative ${draggedSection && draggedSection.id == section.id && "opacity-50"}`}
                      key={itemKey(section.id)}
                    >
                      {!readonly && (
                        <HoveredAddButton
                          title="Add Section"
                          className="-top-4"
                          onClick={() =>
                            handleAddSection(
                              pageLocation === SinglePage
                                ? ReportPageLocationEnums.ReportPart_1
                                : (pageLocation as ReportPageLocationEnums),
                              sectionIndex,
                            )
                          }
                        ></HoveredAddButton>
                      )}
                      <TemplateElementHeader
                        onCollapseChange={handleCollapseChange}
                        sectionKey={section.key ?? ""}
                        fieldKey={""}
                        id={section.id}
                        header={section.title}
                        isDefault={section.isDefault}
                        onDelete={() => handleDeleteSection(section.id)}
                        onDuplicate={(id) => handleDuplicateSection(id)}
                        isCollapsed={getSectionCollapsedState(section.id)}
                        isSection={true}
                        disableDeleteButton={section.fields && section.fields.length > 0}
                        disableDeleteButtonReason="Please delete all fields before deleting this section"
                        onSectionUpdate={(updates) => handleSectionUpdate(section.id, updates)}
                        readonly={readonly}
                      >
                        <SortableContext items={section.fields} strategy={verticalListSortingStrategy}>
                          {section.fields.length > 0 ? (
                            section.fields.map((field, fieldIndex) => (
                              <div
                                className={`template-field-container relative ${draggedField && draggedField.id == field.id && "opacity-50"}`}
                                key={itemKey(field.id)}
                              >
                                {!readonly && (
                                  <HoveredAddButton
                                    title="Add Field"
                                    onClick={() => handleAddField(section.id, fieldIndex)}
                                    className="-top-4"
                                  ></HoveredAddButton>
                                )}
                                <TemplateElementHeader
                                  fieldKey={field.fieldIdentifier}
                                  sectionKey={section.key ?? ""}
                                  id={field.id}
                                  header={field.title ?? ""}
                                  isDefault={field.isDefault}
                                  isAi={field.isAi}
                                  onDelete={() => handleDeleteFieldWithAiCheck(section.id, field.id)}
                                  isCollapsed={getFieldCollapsedState(field.id)}
                                  onDuplicate={(id) => handleDuplicateField(section.id, id)}
                                  onFieldUpdate={(updates) => handleFieldUpdate(section.id, field.id, updates)}
                                  readonly={readonly || !!field.isGlobal}
                                  isGlobal={field.isGlobal ?? false}
                                  exportFramework={reportTemplate.exportFramework}
                                  fieldType={field.fieldType}
                                  onCollapseChange={handleCollapseChange}
                                >
                                  <FieldEditor
                                    readonly={readonly || !!field.isAi}
                                    field={field}
                                    onChange={onChange}
                                    onFieldUpdate={(updatedField) =>
                                      handleFieldUpdate(section.id, field.id, updatedField)
                                    }
                                    setActiveTab={setActiveTab}
                                  />
                                </TemplateElementHeader>
                              </div>
                            ))
                          ) : (
                            <EmptySectionDropZone
                              sectionId={section.id}
                              onAddField={() => handleAddField(section.id, section.fields.length)}
                              readonly={readonly}
                            />
                          )}
                        </SortableContext>
                        <div className="relative">
                          {!readonly && (
                            <HoveredAddButton
                              title="Add Field"
                              onClick={() => handleAddField(section.id, section.fields.length)}
                              className="-top-4"
                            ></HoveredAddButton>
                          )}
                        </div>
                      </TemplateElementHeader>
                    </div>
                  ))
                ) : (
                  <EmptyPageDropZone pageLocation={pageLocation} />
                )}
              </SortableContext>
              <div className="relative">
                {!readonly && (
                  <HoveredAddButton
                    title="Add Section"
                    className="-top-4"
                    onClick={() =>
                      handleAddSection(
                        pageLocation === SinglePage
                          ? ReportPageLocationEnums.ReportPart_1
                          : (pageLocation as ReportPageLocationEnums),
                        sectionsForLocation.length,
                      )
                    }
                  ></HoveredAddButton>
                )}
              </div>
            </div>
          );
        })}
        {createPortal(
          <DragOverlay className="h-11" adjustScale={false}>
            {draggedField ? (
              <TemplateElementHeader
                sectionKey={draggedSection?.key ?? ""}
                fieldKey={draggedField?.fieldIdentifier ?? ""}
                id={draggedField.id}
                header={draggedField.title ?? ""}
                isDefault={draggedField.isDefault}
                isPlaceHolder={true}
                onDelete={() => {}}
                isCollapsed={{ value: true }}
                onDuplicate={() => {}}
                onFieldUpdate={() => {}}
                isGlobal={draggedField.isGlobal ?? false}
                fieldType={draggedField.fieldType}
                onCollapseChange={handleCollapseChange}
              ></TemplateElementHeader>
            ) : draggedSection ? (
              <TemplateElementHeader
                sectionKey={draggedSection.key ?? ""}
                fieldKey={""}
                id={draggedSection.id}
                header={draggedSection.title}
                isDefault={draggedSection.isDefault}
                isPlaceHolder={true}
                isSection={true}
                onDelete={() => {}}
                isCollapsed={{ value: true }}
                onDuplicate={() => {}}
                onFieldUpdate={() => {}}
                onCollapseChange={handleCollapseChange}
              ></TemplateElementHeader>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
      <ConfirmDeleteAiFieldDialog
        open={!!pendingDeleteAiField}
        setOpen={(open) => {
          if (!open) setPendingDeleteAiField(null);
        }}
        fieldTitle={pendingDeleteAiField?.fieldTitle ?? ""}
        onConfirm={() => {
          if (pendingDeleteAiField) {
            handleDeleteField(pendingDeleteAiField.sectionId, pendingDeleteAiField.fieldId);
          }
          setPendingDeleteAiField(null);
        }}
        onCancel={() => setPendingDeleteAiField(null)}
      />
    </div>
  );
};
